<template>
  <div>
    <!-- 나만의 메모 공간 -->
    <div class="mb-2 d-flex align-center justify-space-between">
      <span class="text-subtitle-1 font-weight-medium">
        {{ FormatDate(memo_date) }} 기록
      </span>
      <v-btn icon size="small" color="primary" @click="OpenCalendar" class="hover-premium">
        <v-icon>mdi-calendar</v-icon>
      </v-btn>
    </div>
    
    <v-textarea
      v-model="user_memo"
      label="그 사람에게 하고 싶은 말 / 다짐"
      variant="outlined"
      color="primary"
      rows="3"
      @input="SaveCurrentMemo"
      @focus="OnMemoFocus"
      @blur="OnMemoBlur"
      persistent-hint
      hint="작성하신 내용은 서버에 저장되지 않고 오직 이 기기에만 저장됩니다."
    ></v-textarea>

    <!-- 달력 다이얼로그 -->
    <v-dialog v-model="show_calendar_dialog" max-width="350" transition="scale-transition">
      <v-card style="border-radius: 16px;">
        <v-card-title class="d-flex justify-space-between align-center pt-4">
          <v-btn icon size="small" @click="PrevMonth">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="text-h6 font-weight-bold">
            {{ current_calendar_year }}년 {{ current_calendar_month + 1 }}월
          </span>
          <v-btn icon size="small" @click="NextMonth">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-2">
          <!-- 요일 헤더 -->
          <div style="display: grid; grid-template-columns: repeat(7, 1fr); text-align: center; font-weight: bold; margin-bottom: 8px;">
            <div v-for="day in ['일', '월', '화', '수', '목', '금', '토']" :key="day">
              {{ day }}
            </div>
          </div>
          
          <!-- 일자 그리드 -->
          <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; text-align: center;">
            <div
              v-for="(item, index) in GenerateCalendarDays()"
              :key="index"
              style="position: relative; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; border-radius: 50%;"
              :class="{ 'bg-blue-lighten-4': item.full_date === memo_date }"
              :style="item.is_d_day ? 'border: 2px solid #FF5252; box-sizing: border-box;' : ''"
              @click="item.full_date ? SelectMemoDate(item.full_date) : null"
            >
              <span :class="{ 'text-grey-lighten-1': !item.full_date, 'text-error font-weight-bold': item.is_d_day }">{{ item.day }}</span>
              
              <!-- 노란색 점 -->
              <div
                v-if="item.has_memo"
                style="position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 6px; height: 6px; background-color: #FFD54F; border-radius: 50%;"
              ></div>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="justify-center pb-4">
          <v-btn color="secondary" variant="text" @click="show_calendar_dialog = false">
            닫기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { analytics } from '../firebase';
import { logEvent } from "firebase/analytics";

export default {
  name: 'MemoCalendar',
  props: {
    dDayDate: {
      type: String,
      default: ''
    }
  },
  data() {
    const today = new Date()
    return {
      memo_date: today.toISOString().split('T')[0],
      user_memo: '',
      memo_dates: [],
      show_calendar_dialog: false,
      current_calendar_year: today.getFullYear(),
      current_calendar_month: today.getMonth(),
      start_time: null,
      debounce_timer: null
    }
  },
  mounted() {
    this.MigrateOldMemos();
    this.LoadMemoList();
    this.LoadMemoForDate(this.memo_date);
  },
  methods: {
    MigrateOldMemos() {
      const old_memos_str = localStorage.getItem('parole_calc_memos');
      if (old_memos_str) {
        try {
          const old_memos = JSON.parse(old_memos_str);
          const list_str = localStorage.getItem('parole_calc_memo_list');
          const memo_dates = list_str ? JSON.parse(list_str) : [];
          
          for (const date in old_memos) {
            const memo_content = old_memos[date];
            if (memo_content && memo_content.trim() !== '') {
              localStorage.setItem(`parole_calc_memo_${date}`, memo_content);
              if (!memo_dates.includes(date)) {
                memo_dates.push(date);
              }
            }
          }
          
          localStorage.setItem('parole_calc_memo_list', JSON.stringify(memo_dates));
          localStorage.removeItem('parole_calc_memos');
          /** Analytics event */
          logEvent(analytics, 'data_migration_success');
        } catch (e) {
          console.error('Migration failed:', e);
          /** Analytics event */
          logEvent(analytics, 'data_migration_failed');
        }
      }
    },
    FormatDate(date_string) {
      if (!date_string) return ''
      const date = new Date(date_string)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}년 ${month}월 ${day}일`
    },
    LoadMemoList() {
      const list_str = localStorage.getItem('parole_calc_memo_list')
      this.memo_dates = list_str ? JSON.parse(list_str) : []
    },
    LoadMemoForDate(date_string) {
      this.user_memo = localStorage.getItem(`parole_calc_memo_${date_string}`) || ''
    },
    SaveCurrentMemo() {
      if (this.debounce_timer) {
        clearTimeout(this.debounce_timer);
      }
      this.debounce_timer = setTimeout(() => {
        this.ExecuteSave();
      }, 500);
    },
    ExecuteSave() {
      const trimmed_memo = this.user_memo.trim();
      const date_key = `parole_calc_memo_${this.memo_date}`;
      
      if (trimmed_memo === '') {
        localStorage.removeItem(date_key);
        const index = this.memo_dates.indexOf(this.memo_date);
        if (index > -1) {
          this.memo_dates.splice(index, 1);
          localStorage.setItem('parole_calc_memo_list', JSON.stringify(this.memo_dates));
        }
      } else {
        localStorage.setItem(date_key, trimmed_memo);
        if (!this.memo_dates.includes(this.memo_date)) {
          this.memo_dates.push(this.memo_date);
          localStorage.setItem('parole_calc_memo_list', JSON.stringify(this.memo_dates));
        }
      }
    },
    OpenCalendar() {
      /** Analytics event */
      logEvent(analytics, 'click_calendar_open');
      
      const date = new Date(this.memo_date)
      this.current_calendar_year = date.getFullYear()
      this.current_calendar_month = date.getMonth()
      this.show_calendar_dialog = true
    },
    PrevMonth() {
      if (this.current_calendar_month === 0) {
        this.current_calendar_month = 11
        this.current_calendar_year -= 1
      } else {
        this.current_calendar_month -= 1
      }
    },
    NextMonth() {
      if (this.current_calendar_month === 11) {
        this.current_calendar_month = 0
        this.current_calendar_year += 1
      } else {
        this.current_calendar_month += 1
      }
    },
    GenerateCalendarDays() {
      const year = this.current_calendar_year
      const month = this.current_calendar_month
      
      const first_day = new Date(year, month, 1)
      const last_day = new Date(year, month + 1, 0)
      
      const starting_day_of_week = first_day.getDay()
      const total_days = last_day.getDate()
      
      const days = []
      
      for (let i = 0; i < starting_day_of_week; i++) {
        days.push({ day: '', full_date: null, has_memo: false, is_d_day: false })
      }
      
      for (let i = 1; i <= total_days; i++) {
        const full_date = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`
        const has_memo = this.memo_dates.includes(full_date)
        const is_d_day = full_date === this.dDayDate
        
        days.push({
          day: i,
          full_date: full_date,
          has_memo: has_memo,
          is_d_day: is_d_day
        })
      }
      
      /** 6주(42일)을 항상 채워서 달력 높이 고정 */
      const remaining_slots = 42 - days.length
      for (let i = 0; i < remaining_slots; i++) {
        days.push({ day: '', full_date: null, has_memo: false, is_d_day: false })
      }
      
      return days
    },
    SelectMemoDate(date_string) {
      this.memo_date = date_string
      this.LoadMemoForDate(date_string)
      this.show_calendar_dialog = false
    },
    OnMemoFocus() {
      this.start_time = Date.now();
    },
    OnMemoBlur() {
      if (this.start_time) {
        const duration = (Date.now() - this.start_time) / 1000;
        /** Analytics event */
        logEvent(analytics, 'memo_usage_duration', {
          duration_seconds: duration
        });
        
        logEvent(analytics, 'memo_saved');
        
        this.start_time = null;
      }
    }
  }
}
</script>

<style scoped>
.hover-premium {
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}
.hover-premium:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}
</style>
